import React from 'react';
import {
  Button,
  ButtonText,
  ButtonIcon,
  HStack,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckboxLabel,
  CheckIcon,
  Text,
  Menu,
  MenuItem,
  Switch,
} from '@gluestack-ui/themed';
import { Filter, ChevronDown } from 'lucide-react-native';
import { StateFilterMenuProps } from '../types';

/**
 * StateFilterMenu - A dropdown menu for filtering DataTable rows by lifecycle state.
 *
 * It features:
 * - Multi-select checkboxes for various object states (e.g., Active, Draft, Archived).
 * - Dynamic button label that summarizes the active filters.
 * - A "Show Deprecated" switch for objects that support logical deletion.
 * - Integration with the `lifecycleConfig` defined for each object type.
 * - Visual highlighting when filters are active.
 *
 * @param {StateFilterMenuProps} props - Configuration for available states, current selection, and change handlers.
 * @returns {React.ReactElement | null} The filter menu button and dropdown, or null if no states are defined.
 */
const StateFilterMenu: React.FC<StateFilterMenuProps> = ({
  lifecycleConfig,
  selectedStates,
  onStatesChange,
  includeDeprecated,
  onIncludeDeprecatedChange,
  isActionRunning,
  hideLabel = false,
}) => {
  const handleStateToggle = (stateId: string) => {
    onStatesChange?.((prev) => {
      const isSelected = prev.includes(stateId);
      return isSelected ? prev.filter((s) => s !== stateId) : [...prev, stateId];
    });
  };

  const getStatesLabel = () => {
    if (selectedStates.length === 0) return 'Any State';
    if (selectedStates.length === 1) {
      const state = (lifecycleConfig.states as { id: string; label: string }[]).find((s) => s.id === selectedStates[0]);
      return state?.label || selectedStates[0];
    }
    return `${selectedStates.length} states`;
  };

  const hasActiveFilter = selectedStates.length > 0;

  if (!lifecycleConfig.states) return null;

  return (
    <Menu
      placement="bottom"
      trigger={({ ...triggerProps }) => (
        <Button
          size="sm"
          variant="outline"
          {...triggerProps}
          ml="$2"
          isDisabled={isActionRunning}
          borderRadius="$lg"
          px={hideLabel ? '$2' : '$3'}
          sx={{
            backgroundColor: hasActiveFilter ? '$bgBrandSubtle' : '$white',
            borderColor: hasActiveFilter ? '$borderBrand' : '$borderLight',
          } as unknown as React.ComponentProps<typeof Button>['sx']}
        >
          <ButtonIcon as={Filter} mr={hideLabel ? '$0' : '$2'} color={hasActiveFilter ? '$brandBase' : '$textSubtle'} />
          {!hideLabel && (
            <ButtonText color={hasActiveFilter ? '$textBrand' : '$textBody'} fontWeight="$medium">
              {getStatesLabel()}
            </ButtonText>
          )}
          {!hideLabel && <ButtonIcon as={ChevronDown} ml="$2" color={hasActiveFilter ? '$brandBase' : '$textSubtle'} />}
        </Button>
      )}
    >
      {(lifecycleConfig.states as { id: string; label: string }[]).map((state) => (
        <MenuItem
          key={state.id}
          textValue={state.label}
          closeOnSelect={false}
          onPress={() => handleStateToggle(state.id)}
        >
          <Checkbox
            size="sm"
            value={state.id}
            isChecked={selectedStates.includes(state.id)}
            aria-label={state.label}
            pointerEvents="none"
          >
            <CheckboxIndicator>
              <CheckboxIcon as={CheckIcon} />
            </CheckboxIndicator>
            <CheckboxLabel ml="$2">{state.label}</CheckboxLabel>
          </Checkbox>
        </MenuItem>
      ))}
      {!!lifecycleConfig.supportsDeprecation && (
        <MenuItem
          key="show-deprecated"
          textValue="Show Deprecated"
          closeOnSelect={false}
          onPress={() => onIncludeDeprecatedChange(!includeDeprecated)}
          sx={{
            borderTopWidth: 1,
            borderTopColor: '$backgroundLight300',
            mt: '$2',
            pt: '$3',
          } as unknown as React.ComponentProps<typeof MenuItem>['sx']}
        >
          <HStack alignItems="center" space="sm" w="$full">
            <Switch
              size="sm"
              value={includeDeprecated}
              onValueChange={onIncludeDeprecatedChange}
              isDisabled={isActionRunning}
            />
            <Text size="xs" color="$textSubtle">
              Show Deprecated
            </Text>
          </HStack>
        </MenuItem>
      )}
    </Menu>
  );
};

export default StateFilterMenu;
